import { pageQuery } from '~/queries/page'
import { articleQuery } from '~/queries/article'
import { siteQuery } from '~/queries/site'
import pageEntryQueries from '~/queries/pages'
import globalSetQueries from '~/queries/global-sets'

const sectionEntryTypes = {
    'ds_Events': ['ds_Events_Entry'],
    'ds_News': ['ds_News_Entry'],
    'ds_Newsletters': ['ds_Newsletter_Entry'],
    'ds_NewsletterArticles': ['ds_NewsletterArticle_Entry'],
    'ds_Pages': ['ds_StandardPage_Entry', 'ds_RedirectPage_Entry'],
    'ds_Homepage': ['ds_Homepage_Entry'],
    'ds_Microsites': ['ds_MicrositeHomepage_Entry','ds_MicrositePage_Entry'],
}

export const useSiteStore = defineStore('siteStore', {
    state: () => ({
        entry: {},

        navigation: [],
        globalSets: [],

        siteDetails: {},
        digistormSettings: {},

        seomatic: {},
        variables: {},
        newsListingUrl: '',
        newsletterListingUrl: '',
        eventsListingUrl: '',

        themes: [],
        globalTheme: '',

        adminUrl: '',
        craftUrl: '',
        apiUrl: '',
        fontWeightCss: '',
        themeCss: [],

        bannerHasBorder: false,

        microsite: {},
    }),
    getters: {
        locale(): string {
            const localeData: string = get(this.siteDetails, 'locale', 'en-AU')

            // if the locale is en-US then just return en since that is the us default for dayjs
            if (localeData === 'en-US') {
                return 'en'
            }

            return localeData
        },

        // country code is just the 2 letter code for the country where locale includes language and country
        // au vs en-au, this is just the au part
        countryCode(): string {
            // transform from locale
            return toLower(last(split(get(this.siteDetails, 'locale'), '-')))
        },

        appId(): string {
            return get(this.siteDetails, 'appId', '')
        },

        isMicrosite(): boolean {
            return !isEmpty(this.microsite)
        },

        homeUri(): string {
            if (this.isMicrosite) {
                return `/${this.microsite.uri}`
            }

            return '/'
        },

        getGlobalSetField: (state) => (globalSetType: string, fieldHandle: string, fallback = false) => {
            if (!isEmpty(state.microsite)) {
                const field = get(state.microsite, fieldHandle, null)

                if (!isEmpty(field) || !fallback) {
                    return field
                }
            }

            const globalSet = find(state.globalSets, { __typename: globalSetType })
            return globalSet?.[fieldHandle]
        },

        websiteTier(): string {
            return get(this.digistormSettings, 'websiteTier', 'foundation')
        }
    },
    actions: {
        getPageQuery(isArticle = false) {
            let builtQuery = pageQuery
            if (isArticle) {
                builtQuery = articleQuery
            }

            let pageQueryHelper = ''

            // add possible sections to query
            const enabledSections = get(this.digistormSettings, 'enabledSections', [])

            each(enabledSections, (section) => {
                const sectionTypes = get(sectionEntryTypes, section)

                each(sectionTypes, (entryType) => {
                    builtQuery += get(pageEntryQueries, entryType, '')
                    pageQueryHelper += `
                        ... on ${entryType} {
                            ...${entryType}
                        }
                    `
                })
            })


            builtQuery = replace(builtQuery, '[entryQuery]', pageQueryHelper)

            return gql`${builtQuery}`
        },

        getGlobalSetQuery() {
            let globalSetQuery = ''

            const enabledGlobalSets = map(this.globalSets, (globalSet) => {
                return get(globalSet, '__typename', '')
            })
            
            each(enabledGlobalSets, (globalSet) => {
                globalSetQuery += get(globalSetQueries, globalSet, '')
            })


            return gql`
                query globalSets {
                    globalSets {
                        id
                        name
                        ${globalSetQuery}
                    }
                }
            `
        },

        async loadPage(uri, slug) {
            // reset the entry data, this is needed for CSR
            // otherwise the search page will load the previews pages entry data
            this.entry = {}

            // this will only load the site once
            await callOnce(async () => {
                const { data: siteData, error: siteError } = await useAsyncQuery({
                    query: siteQuery,
                    key: 'siteData',
                    cache: true,
                })

                if (!isEmpty(siteError.value)) {
                    console.error('Error loading site data', siteError.value)
                    return
                }

                this.init(siteData.value)                

                // load globalSets
                const { data: globalSetsData, error: globalSetsError } = await useAsyncQuery({
                    query: this.getGlobalSetQuery(),
                    key: 'globalSetData',
                    cache: true,
                })

                if (!isEmpty(globalSetsError.value)) {
                    console.error('Error loading global-set data', globalSetsError.value)
                    return
                }

                this.initGlobalSets(globalSetsData.value)
            })

            // load colors for error pages
            this.initColorTheme()

            // load font weight variables
            this.initFontWeights()

            // no uri, so return
            if (isEmpty(uri) && isEmpty(slug)) {
                return
            }

            let variables = {
                uri,
                retourUri: `/${uri}`
            }

            // only support newsletter articles for now
            if (!isEmpty(slug)) {
                variables = {
                    slug,
                    type: 'ds_NewsletterArticle'
                }
            }

            // load page data
            const { data: pageData, error: pageError } = await useAsyncQuery({
                query: this.getPageQuery(!!slug),
                variables,
                key: `layout-${uri}`,
                cache: true,
            })

            if (!isEmpty(pageError.value)) {
                console.error('Error loading page data', pageError.value)

                throw createError({
                    statusMessage: 'Something went wrong',
                    message: pageError.value.message ?? 'GraphQL error',
                    statusCode: 500,
                })
            }

            const redirect = get(pageData.value, 'retourResolveRedirect', {})

            if (redirect && !isEmpty(redirect.redirectDestUrl) && redirect.enabled) {
                const route = useRoute()
                return navigateTo({
                    path: redirect.redirectDestUrl,
                    query: route.query
                }, {
                    redirectCode: redirect.redirectHttpCode || 301,
                    replace: true,
                    external: true
                })
            }

            // replace page data entry
            this.entry = get(pageData.value, 'entry', {})

            if (isEmpty(this.entry?.slug)) {
                throw createError({
                    statusMessage: 'Page not found',
                    message: 'The page you are looking for does not exist. <br /><br />Try finding what you are looking for by searching from the top menu or return back to the homepage',
                    statusCode: 404,
                })
            }

            // Handle RedirectPage entries
            if (this.entry?.__typename === 'ds_RedirectPage_Entry') {
                const redirectUrl = this.entry?.redirectLink?.[0].linkUrl

                if (redirectUrl) {
                    return navigateTo({
                        path: redirectUrl,
                        query: useRoute().query
                    }, {
                        redirectCode: 301,
                        replace: true,
                        external: true
                    })
                }
            }

            this.initEntry()
        },

        // load settings for the site and various other settings
        init(data) {
            this.navigation = get(data, 'entries', [])
            this.globalSets = get(data, 'globalSets', [])

            this.themes = get(data, 'colorThemes', [])
            this.globalTheme = get(find(this.themes, { defaultTheme: true }), 'id')

            this.digistormSettings = JSON.parse(get(data, 'digistormComponents', '{}'))
            this.siteDetails = JSON.parse(get(data, 'siteDetails', '{}'))
        },

        initGlobalSets(data) {
            this.globalSets = get(data, 'globalSets', [])
        },

        initEntry() {
            this.seomatic = get(this.entry, 'seomatic', {})
            this.variables = JSON.parse(get(this.seomatic, 'metaSiteVarsContainer', '{}'))

            // check if microsite
            if(['ds_MicrositeHomepage_Entry', 'ds_MicrositePage_Entry'].includes(this.entry.__typename)) {
                this.initMicrosite()
            }
        },
        initMicrosite() {
            this.microsite = this.entry.__typename === 'ds_MicrositeHomepage_Entry' ? this.entry : get(this.entry, 'ancestors[0]', {})

            this.navigation = get(this.microsite, 'children', [])

            // set the theme
            const colorTheme = get(this.microsite, 'colorTheme', '')
            if (!isEmpty(colorTheme)) {
                this.globalTheme = colorTheme
            }
        },

        initColorTheme() {
            // list of deprecated css variables
            // newValue: deprecatedValue
            const deprecatedCssVariables = {
                '--button-brand-primary-surface-default': '--surface-button-primary-1-default',
                '--button-brand-primary-surface-hover': '--surface-button-primary-1-hover',
                '--button-brand-primary-surface-active': '--surface-button-primary-1-active',
                '--button-brand-primary-on-surface-default': '--text-button-primary-default',
            }
            // sort themes so default theme is first to fix css hierarchy issues
            const sortedThemes = sortBy(this.themes, (theme) => !theme.defaultTheme)

            each(sortedThemes, (theme) => {
                let colourCss = ''

                const themeData = JSON.parse(theme?.themeData ?? '{}')

                // add brand information
                const brandData = get(themeData, 'brand', {})
                const accentColor = get(themeData, 'accentColor', '')

                colourCss += `
[data-theme="${theme.id}"] {
`
                forIn(brandData, (value, key) => {
                    colourCss += `    --color-${key}: ${value};\n`
                })

                // create overlay colours
                colourCss += `    --color-overlay-5: ${brandData[700]}0d;\n`
                colourCss += `    --color-overlay-10: ${brandData[700]}1a;\n`
                colourCss += `    --color-overlay-20: ${brandData[700]}33;\n`
                colourCss += `    --color-overlay-30: ${brandData[700]}4d;\n`
                colourCss += `    --color-overlay-40: ${brandData[700]}66;\n`
                colourCss += `    --color-overlay-50: ${brandData[700]}80;\n`
                colourCss += `    --color-overlay-60: ${brandData[700]}99;\n`
                colourCss += `    --color-overlay-70: ${brandData[700]}b2;\n`
                colourCss += `    --color-overlay-80: ${brandData[700]}cc;\n`
                colourCss += `    --color-overlay-90: ${brandData[700]}e5;\n`

                // add accent
                colourCss += `    --color-accent: ${themeData.accentColor};\n`

                colourCss += '}\n'

                const defaultColorValues = {
                    base: {
                        '--color-accent': `${accentColor}`,
                        '--text-subheading': 'var(--color-accent)',
                        '--text-heading': '#1f2937',
                        '--text-body': '#1f2937',
                        '--surface-primary': '#ffffff',
                        '--button-brand-primary-surface-default': 'var(--color-700)',
                        '--button-brand-primary-surface-hover': 'var(--color-800)',
                        '--button-brand-primary-surface-active': 'var(--color-900)',
                        '--button-brand-primary-on-surface-default': '#ffffff',
                    },
                    'color-subtle' : {
                        '--color-accent': `${accentColor}`,
                        '--text-subheading': 'var(--color-accent)',
                        '--text-heading': 'var(--color-700)',
                        '--text-body': '#1f2937',
                        '--surface-primary': 'var(--color-100)',
                        '--button-brand-primary-surface-default': 'var(--color-800)',
                        '--button-brand-primary-surface-hover': 'var(--color-900)',
                        '--button-brand-primary-surface-active': 'var(--color-950)',
                        '--button-brand-primary-on-surface-default': '#ffffff',
                    },
                    'color-invert': {
                        '--color-accent': `${accentColor}`,
                        '--text-subheading': 'var(--color-accent)',
                        '--text-heading': 'var(--color-50)',
                        '--text-body': 'var(--color-50)',
                        '--surface-primary': 'var(--color-900)',
                        '--button-brand-primary-surface-default': '#ffffff',
                        '--button-brand-primary-surface-hover': '#ffffffcc',
                        '--button-brand-primary-surface-active': '#ffffffb2',
                        '--button-brand-primary-on-surface-default': 'var(--color-900)',
                    },
                }

                // loop through all default values
                forIn(defaultColorValues, (cssVariables, key) => {
                    colourCss += `
    [data-theme="${theme.id}"][data-mode*="${key}"],
    [data-theme="${theme.id}"] [data-mode="${key}"] {`

                    forIn(cssVariables, (value, cssVar) => {
                        let newValue = value

                        let deprecatedVar = cssVar
                        // check for deprecated css variables
                        if (deprecatedCssVariables[cssVar]) {
                            deprecatedVar = deprecatedCssVariables[cssVar]
                        }

                        // get value (including deprecated) from theme data
                        const dataValue = get(themeData, `${key}.${deprecatedVar}`)

                        // get value from theme data if it exists
                        if (!isEmpty(dataValue)) {
                            newValue = get(themeData, `${key}.${deprecatedVar}`)
                        }

                        colourCss += `    ${cssVar}: ${newValue};\n`
                    })

                    colourCss += '}\n'
                })

                this.themeCss.push({
                    id: theme.id,
                    css: colourCss,
                })

            })
        },

        initFontWeights() {
            const defaultWeights = this.digistormSettings?.fontWeights.default
            const boldWeights = this.digistormSettings?.fontWeights.richTextBold

            this.fontWeightCss = `html body {
    --typography-weight-heading: ${defaultWeights.heading};
    --typography-weight-subheading: ${defaultWeights.subheading};
    --typography-weight-body: ${defaultWeights.body};
    --typography-weight-button: ${defaultWeights.button};

    --typography-weight-heading-strong: ${boldWeights.heading};
    --typography-weight-subheading-strong: ${boldWeights.subheading};
    --typography-weight-body-strong: ${boldWeights.body};
}`
        }
    },
})
